import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Components
import FloatingPanel from '../../../components/tooltips/FloatingPanel';
import PopupPanelMouseManager from '../../../components/popupPanel/PopupPanelMouseManager';
import SimpleDateTimePicker from '../../../components/datetime/SimpleDateTimePicker';

// Constants
import { PopupIds } from '../../../components/popupPanel/popupConstants';
import { TooltipPositions } from '../../../components/tooltips/tooltipConstants';
import { CellTypeNames } from '../../../../common/table/CellTypeConstants';

// Utils
import {
    dateTimeStringHasSeconds,
    getDateFromString,
    toNumericDateTimeString,
} from '../../../../common/utils/timeUtil';
import { isFormula } from '../utils/dataUtils/formula/tableFormulaPredicateUtils';
import { isNil } from 'lodash';
import { getAllCellsBetween } from '../utils/selectionUtils/tableSelectionGeometryUtils';
import { asObject } from '../../../../common/utils/immutableHelper';
import getEditorJsonText from '../../../../common/tiptap/utils/jsonContentUtils/getEditorJsonText';

import { getHotTableCellData } from '../utils/hotInstanceUtils/meta/hotTableCellMetaUtils';

const DatePickerContainer = (props) => {
    const { isCurrentlyEditingTableCell, gridSize, cellEditorRef, showFormulaDropdown, locale, hotTableInstanceRef } =
        props;

    // Get details from the currently editing cell
    const currentCellSelections = hotTableInstanceRef?.current?.getSelected() || null;
    const cellsBetween = getAllCellsBetween(asObject(currentCellSelections));
    const { row, col } = cellsBetween[0] || {};
    const editingCellData =
        cellsBetween.length === 1
            ? asObject(getHotTableCellData(hotTableInstanceRef?.current, { row, col }))
            : undefined;
    const { type: { hasTime = false, name } = {}, value, draftContent } = editingCellData || {};
    const [hasTimeValue, setHasTimeValue] = useState(hasTime);

    const rawText = draftContent ? getEditorJsonText(draftContent) : value;

    // Get the date from the cell contents, or default to the current date
    const [calendarInputDate, setCalendarInputDate] = useState(
        () => getDateFromString(rawText, locale) || new Date(new Date().setHours(0, 0, 0, 0)),
    );

    const showDatePicker =
        isCurrentlyEditingTableCell && name === CellTypeNames.DATE_TIME && !showFormulaDropdown && !isFormula(rawText);

    useEffect(() => {
        if (isNil(calendarInputDate) || !showDatePicker) return;

        const inlineStyles = editingCellData?.textStyle;

        // when date or hasTime changes, update the cell editor contents
        const currentEditorState = cellEditorRef?.current.getEditorState();
        const content = currentEditorState.getCurrentContent().getPlainText();
        const hasSeconds = dateTimeStringHasSeconds(content);
        const numericDateTimeString = toNumericDateTimeString(
            calendarInputDate,
            {
                hasTime: hasTimeValue,
                hasSeconds,
                useTwelveHour: true,
            },
            locale,
        );

        cellEditorRef?.current.setPlainText(numericDateTimeString, inlineStyles);
    }, [calendarInputDate, hasTimeValue, locale, showDatePicker]);

    if (!showDatePicker) return null;

    return (
        <FloatingPanel
            selector=".Table.selected .handsontable-input-holder"
            className={classNames('TableDateTimePicker', 'className', 'light')}
            position={TooltipPositions.BOTTOM}
            gridSize={gridSize}
            pollPosition
        >
            <PopupPanelMouseManager
                {...props}
                visible={showDatePicker}
                popupId={PopupIds.TABLE_DATE_TIME_PICKER}
                ignoredClicksSelector=".Toolbar"
                closePopup={() => {}}
            >
                <div className="popup-content" data-table-util={true}>
                    <SimpleDateTimePicker
                        locale={locale}
                        date={calendarInputDate}
                        updateDate={(date) => setCalendarInputDate(date)}
                        hasTime={hasTimeValue || false}
                        updateDateHasTime={(hasTime) => setHasTimeValue(hasTime)}
                    />
                </div>
            </PopupPanelMouseManager>
        </FloatingPanel>
    );
};

DatePickerContainer.propTypes = {
    isCurrentlyEditingTableCell: PropTypes.bool,
    gridSize: PropTypes.number,
    element: PropTypes.object,
    updateCellTypeFormat: PropTypes.func,
    cellEditorRef: PropTypes.object,
    showFormulaDropdown: PropTypes.bool,
    locale: PropTypes.string,
    hotTableInstanceRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

export default DatePickerContainer;
